import {
  Backdrop,
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import React, { FC, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import Header from 'src/components/Header';
import Page from 'src/components/Page';
import SimpleAlert from 'src/components/SimpleAlert';
import { useGetStoreByIdQuery } from 'src/features/store/api/storeApi';
import {
  formatActiveWindow,
  lookupModeLabel,
  posSystemLabel,
} from 'src/features/store/utils/lookupConfig';
import { useApiErrorMessage } from 'src/hooks/useApiErrorMessage';
import useAppInsightPageView from 'src/hooks/useAppInsightPageView';
import useCommonStyles from 'src/hooks/useCommonStyles';
import { Theme } from 'src/theme';
import { DataEnumerationsLookupMode } from 'src/types/api/generated/api';
import { removeHTML } from 'src/utils/common';
import { formatDuration } from 'src/utils/duration';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: 100,
  },
  fontWeightMedium: {
    fontWeight: theme.typography.fontWeightMedium as 'bold',
  },
  table: {
    minWidth: 300,
    tableLayout: 'fixed',
    whiteSpace: 'nowrap',
  },
  tableCell: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
}));

const breadcrumbs = [
  {
    name: 'Manage Stores',
    path: '/management/stores',
  },
];

interface DetailFieldProps {
  label: string;
  value: ReactNode;
  classes: ReturnType<typeof useStyles>;
}

const DetailField: FC<DetailFieldProps> = ({ label, value, classes }) => (
  <>
    <TableCell className={classes.fontWeightMedium}>{label}</TableCell>
    <TableCell className={classes.tableCell}>
      <Typography variant="body2" color="textSecondary">
        {value}
      </Typography>
    </TableCell>
  </>
);

const StoreDetailsPage: FC = () => {
  useAppInsightPageView({ name: 'ExoAdmin-View Store' });
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { id } = useParams<{ id: string }>();
  const storeId = Number(id);

  const {
    data: store,
    isLoading,
    error,
  } = useGetStoreByIdQuery(
    { id: storeId },
    { skip: !storeId, refetchOnMountOrArgChange: true },
  );

  const errorMessage = useApiErrorMessage(error);
  const storeZone = store?.timezone ?? '';

  return (
    <Page className={classes.root} title="View Store" exoBgLogo={true}>
      <Backdrop className={commonClasses.backdrop} open={isLoading}>
        <CircularProgress color="primary" />
      </Backdrop>
      <Container maxWidth={false}>
        <Header
          breadcrumbs={breadcrumbs}
          mainHeader={'Management'}
          showCancelButton={true}
          cancelLink={'/app/management/stores'}
        />
        {error && errorMessage && (
          <Box mt={2}>
            <SimpleAlert severity="error" text={errorMessage} />
          </Box>
        )}
        {store && (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={12} sm={12} md={12}>
              <Card>
                <CardHeader title="View Store" />
                <Divider />
                <Table className={classes.table}>
                  <TableBody>
                    <TableRow>
                      <TableCell className={classes.fontWeightMedium}>Store Name</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {store.name}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.fontWeightMedium}>Code</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {store.code}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.fontWeightMedium}>Address Line 1</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {store.address?.address1}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.fontWeightMedium}>Address Line 2</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.address?.address2}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.fontWeightMedium}>City</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {store.address?.city}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.fontWeightMedium}>State</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.address?.state}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.fontWeightMedium}>Zip</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {store.address?.zip}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.fontWeightMedium}>Contact Phone</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.phone}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.fontWeightMedium}>Contact Email</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {store.email}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.fontWeightMedium}>Contact Name</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.contactName}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.fontWeightMedium}>Notes</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.notes ? removeHTML(store.notes) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.fontWeightMedium}>Timezone</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.timezone}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.fontWeightMedium}>
                        Require Station To
                        <br />
                        Trigger Listening
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.startListeningOnlyViaTrigger ? 'Yes' : 'No'}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.fontWeightMedium}>
                        Purchased Item Timeout
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.purchasedItemTimeoutPeriodInSeconds
                            ? formatDuration(store.purchasedItemTimeoutPeriodInSeconds)
                            : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.fontWeightMedium}>
                        Order Idle Timeout
                        <br />
                        In Minutes
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.orderIdleTimeoutInMinutes ?? ''}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.fontWeightMedium}>Data Field 1</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" color="textSecondary">
                          {store.data1}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Box p={1} display="flex" flexDirection="column" alignItems="flex-start" />
              </Card>
            </Grid>
            <Grid item xs={12} lg={12} sm={12} md={12}>
              <Card>
                <CardHeader title="Loyalty Program" />
                <Divider />
                <Table className={classes.table}>
                  <TableBody>
                    <TableRow>
                      <DetailField
                        label="Provider"
                        value={
                          store.loyaltyProgramMode === DataEnumerationsLookupMode.Disabled
                            ? 'No loyalty program'
                            : store.loyaltyProgramProvider
                        }
                        classes={classes}
                      />
                      <DetailField
                        label="Mode"
                        value={lookupModeLabel(store.loyaltyProgramMode)}
                        classes={classes}
                      />
                    </TableRow>
                    <TableRow>
                      <DetailField
                        label="POS System"
                        value={
                          store.loyaltyProgramPosSystem &&
                          posSystemLabel(store.loyaltyProgramPosSystem)
                        }
                        classes={classes}
                      />
                      <DetailField
                        label="Active Window"
                        value={formatActiveWindow(
                          store.loyaltyProgramActiveFrom,
                          store.loyaltyProgramActiveTo,
                          storeZone,
                        )}
                        classes={classes}
                      />
                    </TableRow>
                    <TableRow>
                      <DetailField
                        label="Max Attempts"
                        value={store.loyaltyProgramMaximumAttempts ?? 'Unlimited'}
                        classes={classes}
                      />
                      <DetailField
                        label="Button Label"
                        value={store.loyaltyProgramButtonLabel}
                        classes={classes}
                      />
                    </TableRow>
                    <TableRow>
                      <DetailField
                        label="Button Color"
                        value={store.loyaltyProgramButtonColor}
                        classes={classes}
                      />
                      <DetailField
                        label="Success Message"
                        value={store.loyaltyProgramSuccessMessage}
                        classes={classes}
                      />
                    </TableRow>
                    <TableRow>
                      <DetailField
                        label="Failure Message"
                        value={store.loyaltyProgramFailureMessage}
                        classes={classes}
                      />
                      <DetailField
                        label="Hostname"
                        value={store.loyaltyProgramHostname}
                        classes={classes}
                      />
                    </TableRow>
                    <TableRow>
                      <DetailField
                        label="Username"
                        value={store.hasLoyaltyProgramUsername ? 'Configured' : 'Not configured'}
                        classes={classes}
                      />
                      <DetailField
                        label="Password"
                        value={store.hasLoyaltyProgramPassword ? 'Configured' : 'Not configured'}
                        classes={classes}
                      />
                    </TableRow>
                  </TableBody>
                </Table>
                <Box p={1} display="flex" flexDirection="column" alignItems="flex-start" />
              </Card>
            </Grid>
            <Grid item xs={12} lg={12} sm={12} md={12}>
              <Card>
                <CardHeader title="Discount Codes" />
                <Divider />
                <Table className={classes.table}>
                  <TableBody>
                    <TableRow>
                      <DetailField
                        label="Provider"
                        value={
                          store.discountCodeMode === DataEnumerationsLookupMode.Disabled
                            ? 'Off'
                            : store.discountCodeProvider
                        }
                        classes={classes}
                      />
                      <DetailField
                        label="Mode"
                        value={lookupModeLabel(store.discountCodeMode)}
                        classes={classes}
                      />
                    </TableRow>
                    <TableRow>
                      <DetailField
                        label="POS System"
                        value={
                          store.discountCodePosSystem &&
                          posSystemLabel(store.discountCodePosSystem)
                        }
                        classes={classes}
                      />
                      <DetailField
                        label="Active Window"
                        value={formatActiveWindow(
                          store.discountCodeActiveFrom,
                          store.discountCodeActiveTo,
                          storeZone,
                        )}
                        classes={classes}
                      />
                    </TableRow>
                    <TableRow>
                      <DetailField
                        label="Max Attempts"
                        value={store.discountCodeMaximumAttempts ?? 'Unlimited'}
                        classes={classes}
                      />
                      <DetailField
                        label="Button Label"
                        value={store.discountCodeButtonLabel}
                        classes={classes}
                      />
                    </TableRow>
                    <TableRow>
                      <DetailField
                        label="Button Color"
                        value={store.discountCodeButtonColor}
                        classes={classes}
                      />
                      <DetailField
                        label="Success Message"
                        value={store.discountCodeSuccessMessage}
                        classes={classes}
                      />
                    </TableRow>
                    <TableRow>
                      <DetailField
                        label="Failure Message"
                        value={store.discountCodeFailureMessage}
                        classes={classes}
                      />
                      <DetailField label="" value="" classes={classes} />
                    </TableRow>
                  </TableBody>
                </Table>
                <Box p={1} display="flex" flexDirection="column" alignItems="flex-start" />
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </Page>
  );
};

export default StoreDetailsPage;
